import { Position, PositioningService } from "@spscommerce/positioning";
import clsx from "clsx";
import * as momentImport from "moment-timezone";
import { Moment } from "moment-timezone";
import * as React from "react";

import * as PropTypes from "../prop-types";
import { PropsType } from "../PropsType";
import { spsGlobalPropTypes } from "../util";

const moment = momentImport as any;

export const propTypes = {
    ...spsGlobalPropTypes,
    disabled: PropTypes.bool,
    format: PropTypes.string,
    maxDate: PropTypes.string,
    minDate: PropTypes.string,
    onChange: PropTypes.func,
    onClickOutside: PropTypes.func,
    placeholder: PropTypes.string,
    value: PropTypes.string,
};

export type SpsDatepickerProps = PropsType<typeof propTypes> & React.HTMLAttributes<HTMLButtonElement>;

export function SpsDatepicker(props: SpsDatepickerProps) {
    const {
        className,
        disabled,
        maxDate,
        minDate,
        onChange,
        onClickOutside,
        placeholder,
        "data-testid": testId,
        unsafelyReplaceClassName,
        ...rest
    } = props;
    const _positioningService = new PositioningService();
    const momentValue: Moment = {};

    // States
    const [isOpened, setOpenState] = React.useState(false);
    const [weekdayNames, setWeekdayNames] = React.useState([]);
    const [view, setView] = React.useState(momentValue);
    const [weeks, setWeeks] = React.useState([]);
    const [value, setValue] = React.useState(momentValue);
    const [keyboardFocusDay, setKeyboardFocusDay] = React.useState(momentValue);
    const [text, setText] = React.useState("");
    const [_minDateMoment, setMinDateMoment] = React.useState(minDate ? moment(minDate) : {});
    const [_maxDateMoment, setMaxDateMoment] = React.useState(maxDate ? moment(maxDate) : {});
    const newWeekArray = () => {
        return new Array(7);
    };

    // Refs
    const calendarRef: any = React.useRef();
    const inputRef: any = React.useRef();

    // Effects
    React.useEffect(() => {
        // effect for componentDidMount
        document.addEventListener("mousedown", handleClickOutside);
        setWeekdayNames(Object.freeze(moment.weekdaysShort()));
        setCalendarView(null);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    React.useEffect(() => {
        if (view["_isAMomentObject"]) {
            setWeeks(generateWeeks(view));
        }
    }, [view]);

    React.useEffect(() => {
        if (keyboardFocusDay && keyboardFocusDay._isAMomentObject) {
            if (keyboardFocusDay.isBefore(view, "month")) {
                viewPreviousMonth();
            } else if (keyboardFocusDay.isAfter(view, "month")) {
                viewNextMonth();
            }
        }
    }, [keyboardFocusDay]);

    React.useEffect(() => {
        if (!isOpened) {
            _positioningService.release(calendarRef.current);
        }
    }, [isOpened]);

    React.useEffect(() => {
        if (value && value._isAMomentObject) {setCalendarView(null); }
        if (onChange && typeof onChange === 'function') {onChange(value); }
    }, [value]);

    const show = React.useCallback(
        event => {
            event.stopPropagation();

            if (!isOpened) {
                setOpenState(true);
                _positioningService.position(calendarRef.current, {
                    relativeTo: inputRef.current,
                    position: Position.BOTTOM_RIGHT,
                    offsets: [0, 1]
                });
            }
        },
        [isOpened, setOpenState]
    );

    const hide = event => {
        setOpenState(false);
    };

    const handleClickOutside = event => {
        if (calendarRef && calendarRef.current && !calendarRef.current.contains(event.target)) {
            if (inputRef.current && inputRef.current.contains(event.target)) {
                return;
            }
            if (onClickOutside && typeof onClickOutside === "function") {
                onClickOutside(event);
            }
            hide(event);
        }
    };

    const weekOfMonth = mmt => {
        return Math.floor(
            (mmt
                .clone()
                .date(1)
                .day() +
                mmt.date() -
                1) /
                7
        );
    };

    const generateWeeks = date => {
        const baseDate = {
            year: date.year(),
            month: date.month()
        };

        const weekdayArrayIndex = date.weekday();
        const daysInMonth = date.daysInMonth();
        const weeksInMonth = Math.ceil((daysInMonth + weekdayArrayIndex) / 7);
        const newWeeks = new Array(weeksInMonth).fill(true).map(newWeekArray);

        for (let dateIndex = 0; dateIndex < daysInMonth; dateIndex++) {
            const offsetDay = dateIndex + weekdayArrayIndex;
            const week = newWeeks[Math.floor(offsetDay / 7)];
            const dayOfWeekIndex = offsetDay % 7;
            week[dayOfWeekIndex] = Object.freeze(Object.assign({ date: dateIndex + 1 }, baseDate));

            if (dayOfWeekIndex === 6) {
                Object.freeze(week);
            }
        }
        return newWeeks;
    };

    const setCalendarView = displayMoment => {

        if (!displayMoment || !displayMoment.isValid()) {
            if (value && value.isValid && value.isValid()) {
                displayMoment = value.clone();
            } else if (minDate) {
                displayMoment = _minDateMoment.clone();
            } else if (maxDate) {
                displayMoment = _maxDateMoment.clone();
            } else {
                displayMoment = moment();
            }
        }
        const newView = displayMoment.set({
            date: 1,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0
        });
        setView(newView);
        setWeeks(generateWeeks(newView));
    };

    const parse = (dateValue: string): Moment => {
        const dateAtMidnight = `${dateValue} 00:00:00`;
        return moment(`${dateAtMidnight}${moment(dateAtMidnight).format("Z")}`);
    };

    const onTextChange = e => {
        setValue(parse(e.target.value));
        setText(e.target.value);
    };

    const clearDateValue = e => {
        setValue(null);
        inputRef.current.value = "";
        inputRef.current.focus();
    };

    const selectDate = (e, date) => {
        if (date && !dateIsDisabled(date)) {
            const val = value
                ? moment.isMoment(date)
                    ? date.clone()
                    : value._isAMomentObject
                    ? value.clone().set(date)
                    : moment(date)
                : moment(date);
            setValue(val);
            updateText(val);
            setKeyboardFocusDay(null);
            inputRef.current.focus();
            hide(e);
        }
    };

    const updateText = val => {
        inputRef.current.value = val && val._isAMomentObject ? val.format(props.format) : "";
    };

    const viewPreviousMonth = () => {
        setCalendarView(view.subtract(1, "month"));
    };

    const viewNextMonth = () => {
        setCalendarView(view.add(1, "month"));
    };

    const dateIsDisabled = date => {
        return (
            (date && (minDate && _minDateMoment.isAfter(date, "day"))) ||
            (maxDate && _maxDateMoment.isBefore(date, "day"))
        );
    };

    const dateIsSelected = date => {
        return (
            date && value && value._isAMomentObject && value.isValid() && value.isSame(date, "day")
        );
    };

    const dateIsFocused = date => {
        return (
            date &&
            keyboardFocusDay &&
            keyboardFocusDay._isAMomentObject &&
            keyboardFocusDay.isSame(date, "day")
        );
    };

    const onKeyDown = event => {
        switch (event.key) {
            case "Up":
            case "ArrowUp":
                if (keyboardFocusDay) {
                    event.preventDefault();
                    if (weekOfMonth(keyboardFocusDay) === 0) {
                        setKeyboardFocusDay(null);
                        inputRef.current.focus();
                    } else {
                        setKeyboardFocusDay(
                            moment.max(
                                keyboardFocusDay.clone().subtract(1, "week"),
                                keyboardFocusDay.date(1)
                            )
                        );
                    }
                }
                break;

            case "Down":
            case "ArrowDown":
                event.preventDefault();

                if (!isOpened) {
                    show(event);
                }
                if (keyboardFocusDay && keyboardFocusDay._isAMomentObject) {
                    const newKeyboardFocusDay = moment.min(
                        keyboardFocusDay.clone().add(1, "week"),
                        keyboardFocusDay.date(keyboardFocusDay.daysInMonth())
                    );
                    setKeyboardFocusDay(newKeyboardFocusDay);
                } else {
                    if (view && view._isAMomentObject) {
                        setKeyboardFocusDay(view.clone());
                    }
                }
                break;

            case "Left":
            case "ArrowLeft":
                if (keyboardFocusDay) {
                    event.preventDefault();
                    if (keyboardFocusDay.day() === 0 || keyboardFocusDay.date() === 1) {
                        const week = weekOfMonth(keyboardFocusDay);
                        setKeyboardFocusDay(
                            keyboardFocusDay
                                .subtract(1, "month")
                                .date(1)
                                .day(6)
                        );
                        if (week) {
                            setKeyboardFocusDay(
                                moment.min(
                                    keyboardFocusDay.clone().add(week, "weeks"),
                                    keyboardFocusDay.date(keyboardFocusDay.daysInMonth())
                                )
                            );
                        }
                    } else {
                        setKeyboardFocusDay(keyboardFocusDay.clone().subtract(1, "day"));
                    }
                }
                break;

            case "Right":
            case "ArrowRight":
                if (keyboardFocusDay) {
                    event.preventDefault();
                    if (
                        keyboardFocusDay.day() === 6 ||
                        keyboardFocusDay.date() === keyboardFocusDay.daysInMonth()
                    ) {
                        const week = weekOfMonth(keyboardFocusDay);
                        setKeyboardFocusDay(keyboardFocusDay.add(1, "month").date(1));
                        if (week) {
                            setKeyboardFocusDay(
                                moment.min(
                                    keyboardFocusDay
                                        .clone()
                                        .add(week, "weeks")
                                        .day(0),
                                    keyboardFocusDay.date(keyboardFocusDay.daysInMonth()).day(0)
                                )
                            );
                        }
                    } else {
                        setKeyboardFocusDay(keyboardFocusDay.clone().add(1, "day"));
                    }
                }
                break;

            case "Enter":
                if (keyboardFocusDay) {
                    selectDate(event, keyboardFocusDay);
                }
                break;

            case "Esc":
            case "Escape":
                if (keyboardFocusDay) {
                    setKeyboardFocusDay(null);
                    inputRef.current.focus();
                } else {
                    hide(event);
                }
                break;

            case "Tab":
                hide(event);
                break;

            default:
                if (!isOpened) {
                    show(event);
                }
        }

        if (keyboardFocusDay) {
            if (minDate && _minDateMoment.isAfter(keyboardFocusDay)) {
                setKeyboardFocusDay(_minDateMoment.clone());
            } else if (maxDate && _maxDateMoment.isBefore(keyboardFocusDay)) {
                setKeyboardFocusDay(_maxDateMoment.clone());
            }
        }
    };

    const renderCalendar = () => {
        const calendar = [];
        for (let i = 0; i < weeks.length; i++) {
            const children = [];
            const week = weeks[i];
            for (let j = 0; j < week.length; j++) {
                const date = week[j];

                children.push(
                    <td
                        key={j}
                        className={clsx("sps-datepicker__calendar-cell", {
                            "sps-datepicker__calendar-day": date,
                            "sps-datepicker__calendar-day--selected": dateIsSelected(date),
                            "sps-datepicker__calendar-day--disabled": dateIsDisabled(date),
                            "sps-datepicker__calendar-day--focus": dateIsFocused(date)
                        })}
                        onClick={e => {
                            selectDate(e, date);
                        }}
                    >
                        {date ? date.date : ""}
                    </td>
                );
            }
            calendar.push(<tr key={i}>{children}</tr>);
        }
        return calendar;
    };

    const classes = clsx(
        unsafelyReplaceClassName || "sps-datepicker",
        isOpened && "open",
        isOpened && "z-stratum-dropdown",
        className,
    )

    return (
        <div className={classes}
            data-testid={`${testId}__datePicker`}>
            <span className="sps-datepicker__inputs">
                <span className={clsx("sps-datepicker__input", "sps-form-control", {disabled})}>
                    <span className="sps-datepicker__input-sizer">input sizer</span>
                    <input
                        type="text"
                        ref={inputRef}
                        className="sps-datepicker__text-input"
                        defaultValue={text}
                        onKeyDown={onKeyDown}
                        disabled={disabled}
                        onClick={show}
                        onChange={onTextChange}
                        placeholder={placeholder}
                    />
                </span>
                <i className="sps-icon sps-icon-calendar" />
                {(!disabled && (inputRef && inputRef.current && inputRef.current.value )) && <i
                    className="sps-icon sps-icon-x-circle sps-form-control__clear-btn"
                    onClick={clearDateValue}
                />}
            </span>
            <div className="sps-datepicker__dropdown" ref={calendarRef} tabIndex={-1}>
                <div className="sps-datepicker__calendar">
                    <div className="sps-datepicker__calendar-head">
                        <a
                            className="sps-datepicker__button sps-datepicker__button-previous-month"
                            onClick={viewPreviousMonth}
                        >
                            <i className="sps-icon sps-icon-chevron-left" />
                        </a>
                        <span className="sps-datepicker__calendar-head-label">
                            <span className="sps-datepicker__calendar-head-label-month">
                                {view && view._isAMomentObject ? view.format("MMMM") : ""}
                            </span>
                            <span className="sps-datepicker__calendar-head-label-year">
                                {view && view._isAMomentObject ? view.year() : ""}
                            </span>
                        </span>
                        <a
                            className="sps-datepicker__button sps-datepicker__button-next-month"
                            onClick={viewNextMonth}
                        >
                            <i className="sps-icon sps-icon-chevron-right" />
                        </a>
                    </div>
                    <div className="sps-datepicker__calendar-body">
                        <table>
                            <thead>
                                <tr>
                                    {weekdayNames.map((dayName, i) => {
                                        return <th key={i}>{dayName}</th>;
                                    })}
                                </tr>
                            </thead>
                            <tbody>{renderCalendar().map(e => e)}</tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

Object.assign(SpsDatepicker, {
    propTypes,
    displayName: "SpsDatepicker"
});
